import { AxiosRequestConfig } from 'axios'
import {
  authState,
  codeChallenge,
  codeChallengeMethod,
  contentTypeJson,
  getHeaderParams,
  redirectSynchronyCredit
} from 'helpers/requestParams'
import apiEndpoints from 'const/endPoint'
import apiRequestor from 'services/apiRequestor'
import { LoginParams } from 'typings/authentication'
import config from 'const/config'
import { addBmTelemetryToHeaders } from 'helpers'
import { AuthenticateRequest, AuthenticateResponse } from 'typings/apiRequest'
import { readBlackBox } from 'helpers/windowUtility'
import {
  UpdateContactInfoRequest,
  UserInfoRequest,
  UserInfoResponse
} from './types'

const { APIGEE_CLIENT_ID } = config

const authenticationAPI = {
  authenticate: async ({
    userID,
    password
  }: LoginParams): Promise<AuthenticateResponse> => {
    const url = apiEndpoints.login

    const data: AuthenticateRequest = {
      authInfo: {
        appId: APIGEE_CLIENT_ID,
        redirectUri: redirectSynchronyCredit,
        scope: 'login',
        state: authState,
        codeChallenge,
        codeChallengeMethod
      },
      userInfo: {
        'cipher.password': password,
        userId: userID
      },
      iovation: readBlackBox()
    }

    const requestConfig: AxiosRequestConfig = {
      url,
      method: 'POST',
      headers: addBmTelemetryToHeaders({ 'Content-Type': contentTypeJson }),
      params: { client_id: APIGEE_CLIENT_ID },
      data
    }

    const response = await apiRequestor<AuthenticateResponse>({
      requestConfig,
      hasAccountInfo: false
    })

    return response.data
  },
  getUserInfo: async ({
    accessToken,
    userID,
    ...payload
  }: UserInfoRequest): Promise<UserInfoResponse> => {
    const headerParams = getHeaderParams(accessToken, '', true)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.userInfo,
      method: 'POST',
      headers: { ...headerParams },
      data: { ...payload, userId: userID }
    }

    const response = await apiRequestor<UserInfoResponse>({
      requestConfig,
      moduleType: 'USER'
    })

    return response.data
  },
  /**
   * Service Updates MFA Contact information for the user
   *
   * Documentation: https://confluence.app.syfbank.com/display/CML/User+Info+-+Profile+Update
   */
  updateContactInfo: async ({
    accessToken,
    noRedirect = false,
    userID,
    ...data
  }: UpdateContactInfoRequest) => {
    const headers = getHeaderParams(accessToken, '', true)

    const requestConfig: AxiosRequestConfig = {
      url: apiEndpoints.updateUserContactInfo,
      method: 'PATCH',
      headers: { ...headers },
      data: { ...data, userId: userID }
    }

    // API responds with 204 on success which has no body in the response.
    return apiRequestor<unknown>({
      requestConfig,
      moduleType: 'USER',
      // no-redirect skips api error handling when set to true
      noRedirect
    })
  }
}

export default authenticationAPI
